import { useState } from 'react';

import { Button } from '@/components/Button';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { useTranslation } from '@/i18n/useTranslation';
import { ENROLMENT, useEnrolmentSession } from '@/lib/enrolmentSession';
import { SCREEN, useKioskSession } from '@/lib/kioskSession';
import { confirmTheLastVisitEnd, visitStartedUnconfirmed } from '@/services/liveChannelHandle';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

import { TapIcon } from './TapIcon';
import { WaitingDots } from './WaitingDots';

/**
 * The front door [Figma · 01 · Welcome (tap to start), `12311:9092` · `9124` · `9155`].
 *
 * **One door since 2026-09-02.** The design split this screen: the new-or-returning question moved to
 * `02 · Have you applied?` and this became a single "Tap to start". KID-1's two doors are not gone — they
 * are one screen later, on `HaveYouAppliedScreen`.
 *
 * Rebuilt against the frame. The screen is the same card as every other, with **no header strip** — there is
 * nothing to go back to — a narrower 384px content column than the form screens, and centred type at a
 * larger scale because this is read from further away than a form is filled in.
 *
 * The type scale is **30/38 heading (-0.6 tracking) and 18/29 body**, read from the live frames on
 * 2026-09-01 and identical at all three widths (`12311:9092` desktop · `12311:9124` iPad · `12311:9155`
 * mobile). It was built as 30/34 and 18/28 — a plausible pair, and wrong by 4px on the title and 1px a line
 * on the subtitle. Those 6px are not cosmetic: the content block is `HUG`, so the whole footer sat 6px above
 * where the frame draws it and every box below the heading was reported as drifting. Together they make the
 * `Content` block exactly the drawn 160 (24 + 38 + 8 + 58 + 32).
 *
 * The door only sets copy. `openCheckIn` consumes it and stores a translation key; the door value itself
 * never reaches state, so nothing downstream can diagnose from it [decision 0001].
 */
export function WelcomeScreen() {
  const t = useTranslation();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const goTo = useKioskSession((state) => state.goTo);
  const checkVenueServes = useEnrolmentSession((state) => state.checkVenueServes);
  const dropVisitNumber = useEnrolmentSession((state) => state.dropVisitNumber);
  /*
   * The tap's wait is open: the venue check and the last visit's end, both from the tap. ⛔ **Not a screen** —
   * the welcome stays [03-us-kad-6o; decision 0516] — it dims the door, so a second tap is not a second
   * request, the same treatment the naming step's `namingInFlight` gives its action, and ⭐ the three-dot
   * loader shows beside it for the same span [0516; 03-us-kad-6j-i AC-13].
   */
  const [waiting, setWaiting] = useState(false);

  /*
   * ⭐ **The tap asks the server two things, side by side, before the journey starts.**
   *
   * * **Whether this venue still serves** [03-us-kad-6o AC-2; decision 0430 § 1]. `published: true` starts it;
   *   a 401 or `published: false` has already put the tablet on the plain pad by the time this resolves; ⛔ an
   *   unreadable answer claims no outcome — the welcome stays and the next tap asks again, once [KAD-6i
   *   AC-10's posture].
   * * **That the last visit's end is confirmed** [03-us-kad-6j-i; 0509; 0518]: an end the tablet holds
   *   unconfirmed goes on the end route NOW, naming the visit number held at the tap [0520; AC-18], and the
   *   wait is up to 5 s from the tap. ⛔ Neither call waits for the other: no round trip is added [AC-1].
   *
   * ⭐ **The probe decides WHETHER the journey starts; the confirmation decides WHEN** [AC-6]. So a welcome the
   * probe keeps is no fallback — no line, no count — and only a visit that starts on a `timedOut` wait is one
   * [0521; AC-14]. A visit that starts drops the number read before it [AC-18].
   */
  const start = async (): Promise<void> => {
    setWaiting(true);
    const [serves, end] = await Promise.all([
      checkVenueServes('tap'),
      confirmTheLastVisitEnd(useEnrolmentSession.getState().visitNumber),
    ]);
    setWaiting(false);
    if (!serves) return;
    /*
     * ⛔ **The tablet may have left the enrolled states during the wait** — a late scoped call's 401 lands the
     * pad [0245] while the end call runs out its 5 s — and a visit started then would be a candidate journey on
     * a device with no credential. The same guard `checkVenueServes` and `submitName` hold after their own
     * awaits. A disposed channel says `abandoned`, which is the same answer from the other side.
     */
    if (useEnrolmentSession.getState().enrolment !== ENROLMENT.enrolled) return;
    if (end.outcome === 'abandoned') return;
    if (end.outcome === 'timedOut') visitStartedUnconfirmed(end);
    dropVisitNumber();
    goTo(SCREEN.haveYouApplied);
  };

  /*
   * No Exit, because this screen IS the cleared state — the frame sets its `Exit button` instance
   * `visible: false` [`12438:26978`], and a control that ends a session nobody has started would offer the
   * candidate nothing.
   */
  return (
    <KioskScreen showExit={false}>
      <KioskCard
        contentWidth={384}
        footer={
          /*
           * ONE door, hug width and centred, which is what the frame draws since the design split this
           * screen on 2026-09-02. The two doors moved to `02 · Have you applied?` and live there now.
           *
           * It carries the frame's hand icon [`Icon / Hub / tap`] — drawn at all three widths, and absent
           * from the build until 2026-09-14.
           */
          <div className="flex w-full items-center justify-center">
            {/*
              ⭐ The loader sits BESIDE the door, out of the flow [0516: *"the loader beside the dimmed door"*]:
              absolutely placed 12px right of the button, so the drawn 171×52 door and its 84-tall `Actions`
              band keep the frame's place at all three widths while it shows (at 375 the door leaves 102px on
              its right). Outside the button, so the door's dimming does not dim the dots too.
            */}
            <div className="relative">
              <Button
                disabled={waiting}
                onClick={() => {
                  void start();
                }}
              >
                {/*
                  The hand, before the label and 8px from it — `Button` already carries the frame's `gap-2`,
                  so the drawn 171×52 falls out of the label plus the icon plus the button's own padding.
                */}
                <TapIcon />
                {t('welcome.tapToStart')}
              </Button>
              {waiting && (
                <div className="absolute left-full top-1/2 ml-3 -translate-y-1/2">
                  <WaitingDots />
                </div>
              )}
            </div>
          </div>
        }
      >
        {/*
          `px-0 sm:px-6` rather than a flat `px-6`, because `KioskCard`'s content wrapper is `px-6 sm:px-0`:
          the two own the 24px inset at opposite widths and a flat value here ADDS to the card's below `sm`.
          That put the heading in a 279px column instead of 327 on `01 · Welcome — Mobile` (12021:40340),
          which is narrow enough to wrap the title onto a second line and push the buttons 46px down.
        */}
        <div className="flex w-full flex-col items-center px-0 pb-8 pt-6 text-center text-black sm:px-6">
          {/*
            The title and the subtitle are ONE block, 8px apart, and the block is what the frame draws:
            `Content` (384x160) holds a single child `Heading` (336x104 = 38 + 8 + 58) at all three widths
            [12311:9092 · 12311:9124 · 12311:9155, read 2026-09-02]. Built flat, the two texts were correct
            to the pixel and the group had no counterpart at all, so the check paired the frame's 104-tall
            group against our 38-tall `h1` and reported a 66px difference that was never on the screen —
            which is exactly what the `Heading` departure was suppressing.
          */}
          <div className="flex w-full flex-col items-center gap-2">
            <h1
              className="kiosk-heading-focus w-full text-[30px] font-bold leading-[38px] tracking-[-0.6px]"
              ref={heading}
              tabIndex={-1}
            >
              {t('welcome.title')}
            </h1>
            {/*
              `--muted-foreground`, not the block's `text-black`: the frame draws the subtitle grey (#6b7280)
              against a #111827 title, and every sibling screen — check-in, arrival, finish, submitted —
              already pairs a black heading with a muted subtitle. The registry's grey is #585756 rather than
              the frame's raw hex, so the token stays the source for colour and the frame for everything else.
            */}
            <p className="w-full text-lg leading-[29px] text-[var(--muted-foreground)]">
              {t('welcome.subtitle')}
            </p>
          </div>
        </div>
      </KioskCard>
    </KioskScreen>
  );
}
